/**
 * Level 7, Wake the robot (`wake`): the editor arrives.
 *
 * R12: one line at a time is slow, and the robot needs three things at
 * once to wake. So the lesson opens with the robot asleep (its screen
 * dark, `sleep`) and the four things PEDAGOGY asks for before the task,
 * one idea per beat: the editor, which holds a whole list of instructions
 * (focus on the instrument); that typing alone no longer does anything;
 * Run, which is what does (focus `run`; the button reads “Send to robot”,
 * so that is what the crow calls it); and the three fixtures, one beat
 * each, saying where it is and what kind of value it needs (R5): the lamp
 * lights for `True`, the sign shows words, the battery reads a number.
 * Each one hops as its beat names it, so the eye finds it before the
 * sentence has said where it is.
 * Without the kinds, `power = 0` or `charge = "full"` failed for a reason
 * nobody had said. Then the task, and the outro wakes it.
 *
 * The first lesson judged on a *run* rather than on lines: the robot reads
 * `power`, `name` and `charge` from its memory, and the bay's lamp,
 * nameplate and battery watch them. Finishing is the lesson's last step
 * (invariant 9), so that step must agree with the scene's watches
 * exactly — the robot celebrates on one and the level is recorded on the
 * other, and `power = 0` must earn neither. So the bay is defined here and
 * `awake` *is* the watches' own verdict (`readScene(...).solved`), not a
 * restatement of it that could drift; the activity stands on this scene.
 *
 * No praise and no nudge: the editor reports no line to reply to. The
 * stage's hint strip says which names are still missing, but a name set
 * to the wrong kind (`power = 0`, `charge = "full"`) is not missing, so
 * the strip goes and the fixture just stays dark. That miss is named by
 * the steps instead (R10): once every name is set, one step per fixture,
 * each done when *its own* watch is satisfied (`fixture`, the bay's
 * verdict on that watch alone), so the ask that shows is the one naming
 * the first fixture still wrong. The last step is `awake`, the whole
 * bay's verdict, so finishing is still exactly the watches.
 * The robot wakes on the first outro beat, which says why it could.
 */
import type { MemorySnapshot } from '../../src/memory/model'
import { readScene, type SceneSpec } from '../../src/scene/spec'
import type { Lesson } from './core'

/** The charging bay: the robot, the crow, and three fixtures that watch
 *  the names the robot needs. */
export const BAY: SceneSpec = {
  id: 'bay',
  title: 'Charging bay',
  floor: { at: 80 },
  actors: [
    { id: 'robot', kind: 'robot', x: 50, y: 0, w: 27, stand: true },
    // The crow, so the lesson's lines have someone to say them: on the
    // floor at the far side from the battery, under the lamp, which is
    // mounted high enough on the wall to clear its head.
    { id: 'crow', kind: 'crow', x: 86, y: 0, w: 12, stand: true },
    // Fixtures, not cast: a lamp and a gauge are mounted on the wall
    // and a nameplate hangs above the bay. None of them stands.
    { id: 'lamp', kind: 'lamp', x: 78, y: 20, w: 9 },
    { id: 'battery', kind: 'gauge', x: 20, y: 26, w: 17 },
    { id: 'nameplate', kind: 'sign', x: 50, y: 14, w: 38, label: 'unnamed' },
  ],
  watches: [
    {
      name: 'power',
      effect: { kind: 'lit', actor: 'lamp' },
      hint: 'the lamp is waiting for `power`',
    },
    {
      name: 'name',
      effect: { kind: 'caption', actor: 'nameplate' },
      hint: 'the nameplate is waiting for `name`',
    },
    {
      name: 'charge',
      effect: { kind: 'level', actor: 'battery', max: 100 },
      hint: 'the battery is waiting for `charge` (0 to 100)',
    },
  ],
}

/** Lamp lit, nameplate not blank, battery reading a number: exactly what
 *  the bay's watches say, because it is what they say. */
export const awake = (s: MemorySnapshot): boolean => readScene(BAY, s).solved

/** One fixture doing its job: the bay's verdict on that watch alone. */
const fixture = (name: string) => {
  const watches = BAY.watches.filter((w) => w.name === name)
  return (s: MemorySnapshot): boolean => readScene({ ...BAY, watches }, s).solved
}

/** Every name the bay reads has been given something, right or not. */
const allSet = (s: MemorySnapshot): boolean => readScene(BAY, s).waitingFor.length === 0

export const wake: Lesson = {
  id: 'wake',
  teaches: [],
  steps: [
    {
      beats: [
        { say: 'The robot has nodded off, and it needs three things at once to wake up.', act: [{ actor: 'robot', do: 'sleep' }] },
        { say: 'So here\'s an editor: now you can give it a whole list of instructions.', focus: 'console' },
        { say: 'Typing in it alone does nothing now.', focus: 'console' },
        { say: 'Press “Send to robot”, and it follows the list, top to bottom.', focus: 'run' },
        { say: 'The lamp at the top right lights up when `power` is `True`.', act: [{ actor: 'lamp', do: 'hop' }] },
        { say: 'The sign above the robot shows `name`, so give it words, in quotes.', act: [{ actor: 'nameplate', do: 'hop' }] },
        { say: 'And the battery on the left fills to `charge`, a number from 0 to 100.', act: [{ actor: 'battery', do: 'hop' }] },
      ],
      say: 'Give `power`, `name` and `charge` values, then send the list to the robot.',
      tag: 'you',
      done: ({ snapshot }) => allSet(snapshot),
    },
    {
      say: 'The lamp is still dark: `power` needs to be `True`. Change it, and send the list again.',
      tag: 'you',
      done: ({ snapshot }) => fixture('power')(snapshot),
    },
    {
      say: 'The sign is still blank: give `name` some words, in quotes, and send the list again.',
      tag: 'you',
      done: ({ snapshot }) => fixture('name')(snapshot),
    },
    {
      say: 'The battery can\'t read that: `charge` needs a number from 0 to 100. Change it, and send again.',
      tag: 'you',
      // The last step is the whole bay, so finishing is exactly the watches.
      done: ({ snapshot }) => awake(snapshot),
    },
  ],
  outro: [
    { say: 'Awake! It ran every line, top to bottom, and found all three names.', act: [{ actor: 'robot', do: 'wake' }] },
    { say: 'A list of instructions like that is a program, and you\'ve just written your first.' },
    { say: 'Next, you\'ll read programs other people wrote, and say what they do first.' },
  ],
  takeaway: 'A program is a list of instructions. Send it to the robot, and it runs every one, from top to bottom.',
}
